import React, { useState } from "react";
import { Link } from "react-router-dom";

const AITestGeneration = () => {
  const [userStory, setUserStory] = useState("");
  const [testFramework, setTestFramework] = useState("playwright");
  const [testType, setTestType] = useState("e2e");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTests, setGeneratedTests] = useState("");
  const [showResults, setShowResults] = useState(false);

  const frameworks = [
    { id: "playwright", name: "Playwright", language: "JavaScript/TypeScript" },
    { id: "selenium", name: "Selenium", language: "Java/Python/C#" },
    { id: "cypress", name: "Cypress", language: "JavaScript" },
    { id: "jest", name: "Jest", language: "JavaScript" },
    { id: "junit", name: "JUnit", language: "Java" },
    { id: "pytest", name: "PyTest", language: "Python" },
  ];

  const testTypes = [
    {
      id: "e2e",
      name: "End-to-End Tests",
      description: "Full user journey testing",
    },
    {
      id: "integration",
      name: "Integration Tests",
      description: "Component interaction testing",
    },
    {
      id: "unit",
      name: "Unit Tests",
      description: "Individual function testing",
    },
    { id: "api", name: "API Tests", description: "REST API endpoint testing" },
    {
      id: "performance",
      name: "Performance Tests",
      description: "Load and stress testing",
    },
  ];

  const sampleUserStories = [
    {
      title: "E-commerce Login",
      story:
        "As a user, I want to log into my e-commerce account so that I can view my order history and make purchases. The login should validate email format, require a password, and redirect to the dashboard upon successful authentication.",
    },
    {
      title: "Shopping Cart",
      story:
        "As a customer, I want to add products to my shopping cart, modify quantities, and remove items so that I can manage my purchases before checkout. The cart should persist across browser sessions.",
    },
    {
      title: "User Registration API",
      story:
        "As a developer, I need an API endpoint that accepts user registration data (email, password, name) and returns a success response with user ID, or appropriate error messages for validation failures.",
    },
  ];

  const handleGenerate = async () => {
    if (!userStory.trim()) {
      alert("Please enter a user story or requirement");
      return;
    }

    setIsGenerating(true);
    setShowResults(false);

    // Simulate AI generation with realistic delay
    setTimeout(() => {
      const mockGeneratedTest = generateMockTest(
        userStory,
        testFramework,
        testType
      );
      setGeneratedTests(mockGeneratedTest);
      setIsGenerating(false);
      setShowResults(true);
    }, 3000);
  };

  const generateMockTest = (story, framework, type) => {
    const selectedFramework = frameworks.find((f) => f.id === framework);
    const selectedTestType = testTypes.find((t) => t.id === type);

    if (framework === "playwright" && type === "e2e") {
      return `import { test, expect } from '@playwright/test';

test.describe('${extractFeatureName(story)}', () => {
  test.beforeEach(async ({ page }) => {
    await page.goto('/');
  });

  test('should ${extractTestScenario(story)}', async ({ page }) => {
    // Navigate to login page
    await page.click('[data-testid="login-button"]');
    await expect(page).toHaveURL(/.*login/);

    // Fill in login credentials
    await page.fill('[data-testid="email-input"]', 'user@example.com');
    await page.fill('[data-testid="password-input"]', 'validPassword123');

    // Submit login form
    await page.click('[data-testid="submit-login"]');

    // Verify successful login
    await expect(page).toHaveURL(/.*dashboard/);
    await expect(page.locator('[data-testid="user-welcome"]')).toBeVisible();
    await expect(page.locator('[data-testid="logout-button"]')).toBeVisible();
  });

  test('should handle invalid email format', async ({ page }) => {
    await page.click('[data-testid="login-button"]');
    await page.fill('[data-testid="email-input"]', 'invalid-email');
    await page.fill('[data-testid="password-input"]', 'password123');
    await page.click('[data-testid="submit-login"]');

    await expect(page.locator('[data-testid="email-error"]')).toContainText('Invalid email format');
  });

  test('should handle empty password', async ({ page }) => {
    await page.click('[data-testid="login-button"]');
    await page.fill('[data-testid="email-input"]', 'user@example.com');
    await page.click('[data-testid="submit-login"]');

    await expect(page.locator('[data-testid="password-error"]')).toContainText('Password is required');
  });
});`;
    } else if (framework === "cypress" && type === "e2e") {
      return `describe('${extractFeatureName(story)}', () => {
  beforeEach(() => {
    cy.visit('/');
  });

  it('should ${extractTestScenario(story)}', () => {
    // Navigate to login page
    cy.get('[data-testid="login-button"]').click();
    cy.url().should('include', '/login');

    // Fill in login credentials
    cy.get('[data-testid="email-input"]').type('user@example.com');
    cy.get('[data-testid="password-input"]').type('validPassword123');

    // Submit login form
    cy.get('[data-testid="submit-login"]').click();

    // Verify successful login
    cy.url().should('include', '/dashboard');
    cy.get('[data-testid="user-welcome"]').should('be.visible');
    cy.get('[data-testid="logout-button"]').should('be.visible');
  });

  it('should handle invalid credentials', () => {
    cy.get('[data-testid="login-button"]').click();
    cy.get('[data-testid="email-input"]').type('wrong@example.com');
    cy.get('[data-testid="password-input"]').type('wrongpassword');
    cy.get('[data-testid="submit-login"]').click();

    cy.get('[data-testid="error-message"]').should('contain', 'Invalid credentials');
  });
});`;
    } else if (framework === "jest" && type === "unit") {
      return `import { validateEmail, authenticateUser } from './auth.js';

describe('${extractFeatureName(story)}', () => {
  describe('validateEmail', () => {
    test('should return true for valid email', () => {
      expect(validateEmail('user@example.com')).toBe(true);
      expect(validateEmail('test.email+tag@domain.co.uk')).toBe(true);
    });

    test('should return false for invalid email', () => {
      expect(validateEmail('invalid-email')).toBe(false);
      expect(validateEmail('user@')).toBe(false);
      expect(validateEmail('@domain.com')).toBe(false);
    });
  });

  describe('authenticateUser', () => {
    test('should return user data for valid credentials', async () => {
      const result = await authenticateUser('user@example.com', 'validPassword');
      
      expect(result.success).toBe(true);
      expect(result.user).toHaveProperty('id');
      expect(result.user).toHaveProperty('email', 'user@example.com');
    });

    test('should return error for invalid credentials', async () => {
      const result = await authenticateUser('user@example.com', 'wrongPassword');
      
      expect(result.success).toBe(false);
      expect(result.error).toBe('Invalid credentials');
    });
  });
});`;
    }

    return `// Generated ${selectedTestType.name} for ${selectedFramework.name}
// Based on user story: "${story.substring(0, 100)}..."

// This is a mock implementation. In a real scenario, 
// our AI would analyze your requirements and generate 
// comprehensive test suites tailored to your specific needs.

// Framework: ${selectedFramework.name} (${selectedFramework.language})
// Test Type: ${selectedTestType.name}
// Description: ${selectedTestType.description}`;
  };

  const extractFeatureName = (story) => {
    // Simple extraction - in real AI this would be more sophisticated
    if (story.toLowerCase().includes("login")) return "User Authentication";
    if (story.toLowerCase().includes("cart")) return "Shopping Cart";
    if (story.toLowerCase().includes("register")) return "User Registration";
    return "Feature Tests";
  };

  const extractTestScenario = (story) => {
    // Simple extraction - in real AI this would be more sophisticated
    if (story.toLowerCase().includes("login"))
      return "allow user to login successfully";
    if (
      story.toLowerCase().includes("add") &&
      story.toLowerCase().includes("cart")
    )
      return "add products to shopping cart";
    return "fulfill the user requirements";
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedTests);
    alert("Test code copied to clipboard!");
  };

  return (
    <main className="container mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <Link
          to="/"
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Home
        </Link>

        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
          AI-Powered <span className="text-gradient">Test Generation</span>
        </h1>

        <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
          Our platform uses Generative AI to automatically generate test cases
          from your requirements, saving you countless hours of manual work.
          Simply provide a user story, and our AI will produce a comprehensive
          suite of tests.
        </p>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-2xl font-semibold text-white mb-4">
                📝 User Story / Requirements
              </h2>

              {/* Sample Stories */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">
                  Try a sample story:
                </label>
                <div className="space-y-2">
                  {sampleUserStories.map((sample, index) => (
                    <button
                      key={index}
                      onClick={() => setUserStory(sample.story)}
                      className="w-full text-left p-3 hover:bg-gray-700 rounded-md transition text-sm sample-story-btn"
                    >
                      <div className="font-medium text-white sample-story-title">
                        {sample.title}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 sample-story-desc">
                        {sample.story.substring(0, 80)}...
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={userStory}
                onChange={(e) => setUserStory(e.target.value)}
                placeholder="Enter your user story or requirements here..."
                className="w-full h-32 p-4 text-white rounded-md border border-gray-700 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>

            <div className="card p-6">
              <h3 className="text-xl font-semibold text-white mb-4">
                ⚙️ Test Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Test Framework
                  </label>
                  <select
                    value={testFramework}
                    onChange={(e) => setTestFramework(e.target.value)}
                    className="w-full p-3 text-black rounded-md border border-gray-700 focus:border-blue-500 focus:outline-none"
                  >
                    {frameworks.map((framework) => (
                      <option key={framework.id} value={framework.id}>
                        {framework.name} ({framework.language})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Test Type
                  </label>
                  <select
                    value={testType}
                    onChange={(e) => setTestType(e.target.value)}
                    className="w-full p-3  text-black rounded-md border border-gray-700 focus:border-blue-500 focus:outline-none"
                  >
                    {testTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={isGenerating || !userStory.trim()}
                className="w-full mt-6 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed ai-generate-btn"
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Generating Tests...
                  </div>
                ) : (
                  "🚀 Generate Tests"
                )}
              </button>
            </div>
          </div>

          {/* Output Panel */}
          <div className="space-y-6">
            {showResults ? (
              <div className="card p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold text-white">
                    🤖 Generated Tests
                  </h2>
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition text-sm ai-copy-btn"
                  >
                    📋 Copy Code
                  </button>
                </div>

                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-green-400">
                    <code>{generatedTests}</code>
                  </pre>
                </div>

                <div className="mt-4 p-4 bg-blue-900/50 rounded-lg">
                  <h4 className="text-white font-medium mb-2">
                    💡 What's Generated:
                  </h4>
                  <ul className="text-sm text-blue-300 space-y-1">
                    <li>• Complete test suite structure</li>
                    <li>• Positive and negative test scenarios</li>
                    <li>• Proper assertions and validations</li>
                    <li>• Framework-specific syntax and best practices</li>
                    <li>• Error handling and edge cases</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="card p-6 text-center">
                <div className="text-6xl mb-4">🤖</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  AI Test Generator Ready
                </h3>
                <p className="text-gray-400">
                  Enter your user story and configure your preferred test
                  framework to get started. Our AI will analyze your
                  requirements and generate comprehensive test cases.
                </p>
              </div>
            )}

            {/* Features */}
            <div className="card p-6">
              <h3 className="text-xl font-semibold text-white mb-4">
                ✨ Features
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Multi-framework support
                  </div>
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Edge case generation
                  </div>
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Best practices included
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Ready-to-run code
                  </div>
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Multiple test types
                  </div>
                  <div className="flex items-center text-green-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Intelligent analysis
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AITestGeneration;
